import {
  ageShort,
  branchName,
  can,
  deployRuleLabel,
  deployRuleProblem,
  fmtDate,
  githubRef,
  prChip,
  projectCode,
  REVIEW_WORDS,
  SCAN_COMMITS,
  type BoardItem,
  type BoardPr,
  type BoardReposView,
  type DeployRule,
  type GitHubRepoView,
  type GitHubStatus,
  type ProjectReposView,
  type RepoHit,
  type RepoScanApplied,
  type RepoScanView,
  type ScanItemChange,
  type UnlinkedPr,
  type Viewer,
} from "@boem/core/shared";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError } from "../api.ts";
import { githubApi, postManifest, repoShort } from "../prs.ts";
import "../github.css";
import { Icon } from "./Icons.tsx";
import { ErrorLine, Modal } from "./Modal.tsx";

// GitHub on the board (docs/github.md), OEMUAP only: the PR chips in a card's badge row and the
// GitHub block in its panel, Settings → GitHub (admins: Connect, the status, the repos, people's GitHub
// usernames, Disconnect; the repos are the one place for repo facts: what uses each (the main board,
// projects), its deploy signal, its autolink, a whole-board scan; leads see the repos to link them to
// projects), a project's Repos (leads: which repos, how each deploys read-only, a scan of the
// project's items), Link a PR by its address, and the recent PRs a project's board can attach.

const say = (e: unknown) => (e instanceof ApiError ? e.message : "Couldn't reach the server. Try again.");

/** A PR's chip: "PR 418 · open 3d · draft", coloured by its state; it opens the PR on GitHub. */
export function PrChip({ pr, now }: { pr: BoardPr; now?: Date }) {
  const open = pr.state === "open";
  const tone =
    pr.state === "merged"
      ? "merged"
      : pr.state === "closed"
        ? "closed"
        : pr.ci === "failure"
          ? "failing"
          : pr.draft
            ? "draft"
            : pr.review === "changes_requested"
              ? "changes"
              : pr.review === "approved"
                ? "approved"
                : "open";
  const waiting = open && !pr.draft && pr.waitingFor?.length ? pr.waitingFor.map((w) => w.name).join(", ") : null;
  const title = [
    `${pr.repo} · PR ${pr.number}: ${pr.title}`,
    `by ${pr.author}`,
    pr.state === "merged" ? `merged into ${pr.baseRef}${pr.intoDefault ? "" : " (not the default branch)"}` : open ? `opened ${fmtDate(pr.openedAt)}, last activity ${ageShort(pr.lastActivityAt, now)} ago` : "closed without merging",
    open && pr.draft ? "a draft: the item stays To do until it's ready for review" : null,
    waiting ? `waiting for review by ${waiting}${pr.readyAt ? ` (ready ${ageShort(pr.readyAt, now)} ago)` : ""}${pr.reviewStale ? ", stale" : ""}` : null,
    open && pr.review ? REVIEW_WORDS[pr.review] : null,
    pr.deployedIn ? `deployed in ${pr.deployedIn}` : null,
    pr.moves ? null : "its repo isn't linked to the project, so the board doesn't move the item for it",
  ]
    .filter(Boolean)
    .join("\n");
  return (
    <a className="prchip" data-tone={tone} data-stale={pr.stale ? "1" : undefined} href={pr.url} target="_blank" rel="noopener noreferrer" title={title}>
      <Icon name="pr" />
      <span>{prChip(pr, now)}</span>
    </a>
  );
}

/** The chips in a card's badge row (at most three, then "+n"). */
export function PrChips({ prs }: { prs: BoardPr[] | undefined }) {
  if (!prs?.length) return null;
  const shown = prs.slice(-3);
  return (
    <>
      {shown.map((p) => (
        <PrChip key={p.id} pr={p} />
      ))}
      {prs.length > shown.length && <span className="role">+{prs.length - shown.length} PRs</span>}
    </>
  );
}

/** A card's GitHub block (in its panel): each PR with what the board does with it, and the item's branch name. */
export function GitHubBlock({ it, viewer, onLink, onUnlink, onCopy }: { it: BoardItem; viewer: Viewer; onLink?: () => void; onUnlink?: (pr: BoardPr) => void; onCopy: (text: string, what: string) => void }) {
  const branch = branchName(it);
  const mayLink = can(viewer, "github") && !it.archived;
  return (
    <section className="gh-block" aria-label={`GitHub: ${githubRef(it.num)}`}>
      <div className="gh-head">
        <span className="fl">GitHub</span>
        <button type="button" className="gh-copy" title="Copy the branch name" onClick={() => onCopy(branch, "the branch name")}>
          <code>{branch}</code>
        </button>
      </div>
      {it.prs?.length ? (
        <ul className="gh-prs">
          {it.prs.map((p) => (
            <li key={p.id}>
              <PrChip pr={p} />
              <span className="gh-t">{p.title}</span>
              <span className="gh-sub">
                {repoShort(p.repo)} · {p.author}
                {p.state === "open" && !p.draft && p.waitingFor?.length ? ` · waiting for review by ${p.waitingFor.map((w) => w.name).join(", ")}` : ""}
                {p.state === "open" && p.draft ? " · a draft: the item stays To do until it's ready for review" : ""}
                {p.deployedIn ? ` · deployed in ${p.deployedIn}` : ""}
                {p.moves ? "" : " · its repo isn't linked to the project: the board won't move this item for it"}
              </span>
              {mayLink && onUnlink && (
                <button type="button" className="chip sm rm" onClick={() => onUnlink(p)} aria-label={`Unlink PR ${p.number}`}>
                  Unlink
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="gh-none">
          No pull request yet. Name the branch <code>{branch}</code>, or put <code>{githubRef(it.num)}</code> in the PR&rsquo;s title or description.
        </p>
      )}
      {mayLink && onLink && (
        <button type="button" className="chip sm" onClick={onLink}>
          Link a PR&hellip;
        </button>
      )}
    </section>
  );
}

/** Link a PR by its address. */
export function LinkPrModal({ item, onClose, onSaved, toast }: { item: BoardItem; onClose: () => void; onSaved: () => void; toast: (m: string) => void }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await githubApi.linkPr(item.num, url.trim());
      toast(`Linked PR ${r.pr} (${repoShort(r.repo)}) to ${githubRef(item.num)}.`);
      onSaved();
      onClose();
    } catch (e) {
      setError(say(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal onClose={onClose}>
      <div className="gh-dlg">
        <h3>Link a pull request to {githubRef(item.num)}</h3>
        <p className="gh-lead">Paste its address. A PR whose branch, title or description says {githubRef(item.num)} is linked by itself; this is for the ones that don&rsquo;t.</p>
        <label htmlFor="gh-pr-url">Pull request</label>
        <input id="gh-pr-url" type="text" value={url} placeholder="https://github.com/oemuap/mycom-next/pull/418" onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && url.trim() && submit()} />
        <ErrorLine error={error} />
        <div className="row">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn primary" disabled={busy || !url.trim()} onClick={submit}>
            Link it
          </button>
        </div>
      </div>
    </Modal>
  );
}

// -- Settings → GitHub ---------------------------------------------------------------------

/** What the URL said after GitHub sent the admin back (?connected=, ?installed=1, ?error=). */
export interface SettingsNotice {
  connected?: string | null;
  installed?: boolean;
  error?: string | null;
}

function Login({ person, onSaved }: { person: GitHubStatus["people"][number]; onSaved: () => void }) {
  const [v, setV] = useState(person.githubLogin ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setV(person.githubLogin ?? ""), [person.githubLogin]);
  const dirty = v.trim() !== (person.githubLogin ?? "");
  const save = async () => {
    if (!dirty) return;
    setBusy(true);
    setError(null);
    try {
      await githubApi.setLogin(person.id, v.trim() || null);
      onSaved();
    } catch (e) {
      setError(say(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li>
      <span className="nm">{person.name}</span>
      <input
        type="text"
        aria-label={`${person.name}'s GitHub username`}
        placeholder="GitHub username"
        value={v}
        disabled={busy}
        onChange={(e) => setV(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => e.key === "Enter" && save()}
      />
      {error && <span className="gh-err">{error}</span>}
    </li>
  );
}

/** A project as a chip names it: "01 Checkout". */
const projectName = (p: { projectNo: number | null; title: string }) => (p.projectNo != null ? `${projectCode(p.projectNo)} ${p.title}` : p.title);

/** "Deployed isn't detected": the repo is used, with no deploy signal, so its items stop at Developed. */
function NoDeployFlag() {
  return (
    <span className="gh-flag" title="Something uses this repo but it has no deploy signal: its items stop at Developed. Set how it deploys in Settings → GitHub.">
      Deployed isn&rsquo;t detected
    </span>
  );
}

/**
 * What uses a repo, and (Settings → GitHub) changing it: the main board (admins) and the projects
 * (leads: link with the picker, unlink with ×). The same mapping as Project settings → Repos.
 */
function UsedBy({ r, projects, viewer, onChanged, toast }: { r: GitHubRepoView; projects: BoardReposView["projects"]; viewer: Viewer; onChanged: () => void; toast: (m: string) => void }) {
  const admin = can(viewer, "admin");
  const leads = can(viewer, "project");
  const [busy, setBusy] = useState(false);
  const linked = new Set(r.projects.map((p) => p.num));
  const free = projects.filter((p) => !linked.has(p.num));
  const short = repoShort(r.fullName);
  const act = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true);
    try {
      await fn();
      toast(done);
      onChanged();
    } catch (e) {
      toast(say(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="gh-used" role="group" aria-label={`What uses ${r.fullName}`}>
      <span className="gh-used-l">Used by</span>
      {admin ? (
        <button
          type="button"
          className="chip sm gh-main"
          aria-pressed={!!r.mainBoard}
          disabled={busy}
          title={r.mainBoard ? `Its PRs move the items in no project${r.mainBoard.by ? ` (linked by ${r.mainBoard.by})` : ""}. Press to unlink it.` : "Link it to the main board: its PRs then move the items in no project"}
          onClick={() => act(() => githubApi.setMainBoard(r.id, !r.mainBoard), r.mainBoard ? `Took ${short} off the main board.` : `Linked ${short} to the main board.`)}
        >
          {r.mainBoard && <Icon name="check" />} Main board
        </button>
      ) : (
        r.mainBoard && (
          <span className="chip sm gh-main" data-on="1" title="Its PRs move the items in no project (an admin changes this)">
            Main board
          </span>
        )
      )}
      {r.projects.map((p) => (
        <span key={p.num} className="chip sm gh-proj">
          {projectName(p)}
          {leads && (
            <button type="button" className="gh-x" disabled={busy} aria-label={`Unlink ${r.fullName} from ${projectName(p)}`} title="Unlink it from this project" onClick={() => act(() => githubApi.removeRepo(p.num, r.id), `Took ${short} off ${projectName(p)}.`)}>
              <Icon name="close" />
            </button>
          )}
        </span>
      ))}
      {!r.mainBoard && !r.projects.length && <span className="gh-sub">nothing</span>}
      {leads && free.length > 0 && (
        <select
          className="chip sm gh-pick"
          aria-label={`Link ${r.fullName} to a project`}
          value=""
          disabled={busy}
          onChange={(e) => {
            const num = Number(e.target.value);
            const p = projects.find((x) => x.num === num);
            if (p) void act(() => githubApi.addRepo(num, r.id), `Linked ${short} to ${projectName(p)}.`);
          }}
        >
          <option value="">+ Project&hellip;</option>
          {free.map((p) => (
            <option key={p.num} value={p.num}>
              {projectName(p)}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

/** A repo in Settings → GitHub: what uses it, how it deploys, its autolink, and a whole-board scan. */
function BoardRepoRow({
  r,
  view,
  viewer,
  autolink,
  onChanged,
  toast,
  scan = null,
}: {
  r: GitHubRepoView;
  view: BoardReposView;
  viewer: Viewer;
  autolink: GitHubStatus["autolink"] | null;
  onChanged: () => void;
  toast: (m: string) => void;
  scan?: RepoScanView | null;
}) {
  const admin = can(viewer, "admin");
  const copy = (text: string, what: string) =>
    navigator.clipboard.writeText(text).then(
      () => toast(`Copied ${what}.`),
      () => toast(`Copy ${what} by hand: ${text}`),
    );
  return (
    <li className="gh-brepo">
      <span className="nm">{r.fullName}</span>
      <span className="gh-al" data-ok={r.autolink.confirmed ? "1" : undefined}>
        {r.autolink.confirmed ? (
          <>
            <Icon name="check" /> Autolink{r.autolink.by ? ` (${r.autolink.by})` : ""}
          </>
        ) : (
          "Autolink missing"
        )}
      </span>
      <span className="gh-sub">
        default branch {r.defaultBranch} · {r.private ? "private" : "public"}
        {r.archived ? " · archived" : ""}
        {!r.inInstallation ? " · no longer in the installation" : ""}
      </span>
      <UsedBy r={r} projects={view.projects} viewer={viewer} onChanged={onChanged} toast={toast} />
      {r.noDeploy && <NoDeployFlag />}
      {admin ? <DeployEditor repo={r} onSaved={() => (onChanged(), toast(`Saved how ${repoShort(r.fullName)} deploys.`))} /> : <span className="gh-sub gh-deployline">Deploys: {deployRuleLabel(r.deploy)}</span>}
      {admin && autolink && (
        <span className="gh-acts">
          <a className="chip sm" href={r.autolinkUrl} target="_blank" rel="noopener noreferrer" title={`Add an autolink: prefix ${autolink.prefix}, URL ${autolink.urlTemplate}`}>
            Autolink settings <Icon name="goto" />
          </a>
          <button type="button" className="chip sm" onClick={() => copy(autolink.prefix, "the prefix")}>
            Copy prefix
          </button>
          <button type="button" className="chip sm" onClick={() => copy(autolink.urlTemplate, "the URL template")}>
            Copy URL
          </button>
          <label className="gh-check">
            <input type="checkbox" checked={r.autolink.confirmed} onChange={(e) => githubApi.confirmAutolink(r.id, e.target.checked).then(onChanged, (err) => toast(say(err)))} /> It&rsquo;s set up
          </label>
        </span>
      )}
      {admin && view.connected && <RepoScan projectNum={null} repo={r} toast={toast} onApplied={onChanged} initial={scan} />}
    </li>
  );
}

type RepoFilter = "all" | "used" | "unused" | "attention";
const REPO_FILTERS: { id: RepoFilter; label: string }[] = [
  { id: "all", label: "All repos" },
  { id: "used", label: "In use" },
  { id: "unused", label: "Not in use" },
  { id: "attention", label: "Needs attention" },
];
const usedRepo = (r: GitHubRepoView) => !!r.mainBoard || r.projects.length > 0;

/** Does a repo pass the GitHub tab's search and filter? Pure. */
export function repoShown(r: GitHubRepoView, q: string, f: RepoFilter): boolean {
  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const hay = `${r.fullName} ${r.projects.map(projectName).join(" ")}${r.mainBoard ? " main board" : ""}`.toLowerCase();
  if (!words.every((w) => hay.includes(w))) return false;
  if (f === "used") return usedRepo(r);
  if (f === "unused") return !usedRepo(r);
  if (f === "attention") return r.noDeploy || (usedRepo(r) && !r.autolink.confirmed);
  return true;
}

/**
 * Settings → GitHub's repos, the one place for repo facts: every repo of the installation once, with
 * what uses it (the main board, projects), its deploy signal, its autolink and a whole-board scan; a
 * search and a filter, since the organisation may have many.
 */
function BoardRepos({
  viewer,
  autolink,
  toast,
  initial = null,
  scans = null,
}: {
  viewer: Viewer;
  autolink: GitHubStatus["autolink"] | null;
  toast: (m: string) => void;
  initial?: BoardReposView | null;
  scans?: Record<string, RepoScanView> | null;
}) {
  const [v, setV] = useState<BoardReposView | null>(initial);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [f, setF] = useState<RepoFilter>("all");
  const load = useCallback(() => githubApi.boardRepos().then(setV, (e) => setError(say(e))), []);
  useEffect(() => {
    load();
  }, [load]);
  const shown = useMemo(() => (v?.repos ?? []).filter((r) => repoShown(r, q, f)), [v, q, f]);
  const admin = can(viewer, "admin");
  const attention = (v?.repos ?? []).filter((r) => r.noDeploy).length;
  return (
    <section className="gh-brepos" aria-label="Repositories">
      <div className="fl">
        Repositories &middot; {v ? (shown.length === v.repos.length ? v.repos.length : `${shown.length} of ${v.repos.length}`) : "…"}
      </div>
      <p className="gh-sub">
        The organisation&rsquo;s repos, each once. <b>Used by</b> says whose items its PRs move: the <b>main board</b> moves the items in no project; a <b>project</b> moves its own items (an item in a project moves only for its project&rsquo;s repos).
        {admin ? (
          <>
            {" "}
            Its deploy signal is set here only, and every project the repo is in shares it.
            {autolink && (
              <>
                {" "}
                Each repo needs an autolink so <code>{autolink.prefix}185</code> links back here: prefix <code>{autolink.prefix}</code>, URL <code>{autolink.urlTemplate}</code>. The read-only app can&rsquo;t set it or read it: add it on GitHub, then tick it here.
              </>
            )}
          </>
        ) : (
          " You can link a repo to a project or unlink it; deploy signals, autolinks and the main board are an admin's."
        )}
      </p>
      {attention > 0 && (
        <p className="gh-warn">
          {attention === 1 ? "1 repo in use has" : `${attention} repos in use have`} no deploy signal: Deployed isn&rsquo;t detected, so {attention === 1 ? "its" : "their"} items stop at Developed.
        </p>
      )}
      <ErrorLine error={error} />
      {v && v.listed === "board" && v.connected && <p className="gh-sub">GitHub didn&rsquo;t answer: these are the repos the board knows.</p>}
      {v && v.repos.length > 0 && (
        <div className="gh-filter">
          <input className="search" type="search" placeholder="Search repos and projects…" aria-label="Search the repos" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="chip sm" aria-label="Which repos" value={f} onChange={(e) => setF(e.target.value as RepoFilter)}>
            {REPO_FILTERS.map((x) => (
              <option key={x.id} value={x.id}>
                {x.label}
              </option>
            ))}
          </select>
        </div>
      )}
      {!v ? (
        !error && <div className="empty">Loading the repos&hellip;</div>
      ) : v.repos.length ? (
        shown.length ? (
          <ul className="ppl gh-repos">
            {shown.map((r) => (
              <BoardRepoRow key={r.id} r={r} view={v} viewer={viewer} autolink={autolink} onChanged={load} toast={toast} scan={scans?.[r.id] ?? null} />
            ))}
          </ul>
        ) : (
          <p className="gh-none">No repo matches.</p>
        )
      ) : (
        <p className="gh-none">{v.connected ? "No repositories in the installation yet." : "No repositories yet: they show here once GitHub is connected and the app is installed."}</p>
      )}
    </section>
  );
}

/**
 * The board's settings (OEMUAP), reached from the main board's cogwheel or the board switcher. One tab
 * for now: GitHub. Admins have all of it; leads see the repos, to link them to projects.
 */
export function SettingsModal({
  onClose,
  toast,
  notice,
  viewer,
  initial = null,
  initialRepos = null,
  scans = null,
}: {
  onClose: () => void;
  toast: (m: string) => void;
  notice?: SettingsNotice | null;
  viewer: Viewer;
  initial?: GitHubStatus | null;
  /** tests: the repos already loaded */
  initialRepos?: BoardReposView | null;
  /** tests: a scan already shown, by repo id */
  scans?: Record<string, RepoScanView> | null;
}) {
  const admin = can(viewer, "admin");
  const [s, setS] = useState<GitHubStatus | null>(initial);
  const [error, setError] = useState<string | null>(notice?.error ?? null);
  const [busy, setBusy] = useState(false);
  const [owner, setOwner] = useState(initial?.defaultOwner ?? "");
  const [name, setName] = useState("OEMUAP Board");
  const [stale, setStale] = useState(String(initial?.app?.staleDays ?? 3));
  const load = useCallback(
    () =>
      admin
        ? githubApi.settings().then(
            (x) => {
              setS(x);
              setOwner((o) => o || x.defaultOwner);
              setStale(String(x.app?.staleDays ?? 3));
            },
            (e) => setError(say(e)),
          )
        : Promise.resolve(),
    [admin],
  );
  useEffect(() => {
    load();
  }, [load]);
  const act = async (fn: () => Promise<unknown>, done?: string) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
      if (done) toast(done);
    } catch (e) {
      setError(say(e));
    } finally {
      setBusy(false);
    }
  };
  const connect = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await githubApi.connect(owner.trim(), name.trim());
      postManifest(r.postUrl, r.manifest);
    } catch (e) {
      setError(say(e));
      setBusy(false);
    }
  };
  const app = s?.app;
  const people = (s?.people ?? []).filter((p) => !p.isBot);

  return (
    <Modal onClose={onClose} wide>
      <div className="org-dlg gh-dlg">
        <div className="dlg-eyebrow">
          <span>OEMUAP</span>
          <span>Settings</span>
        </div>
        <h3>Settings</h3>
        <div className="gh-tabs" role="tablist" aria-label="Settings">
          <button type="button" role="tab" aria-selected="true" className="chip sm" aria-pressed="true">
            <Icon name="pr" /> GitHub
          </button>
        </div>
        {notice?.connected && !error && <p className="gh-ok">The app {notice.connected} is made. Now install it on all of the organisation&rsquo;s repositories.</p>}
        {notice?.installed && !error && <p className="gh-ok">Installed. Below, link repos to the main board or to projects, and set how each deploys.</p>}
        <ErrorLine error={error} />
        {!admin ? (
          <>
            <p className="gh-lead">Which repos move which items. Link a repo to a project here or in its Project settings; it&rsquo;s the same link.</p>
            <BoardRepos viewer={viewer} autolink={null} toast={toast} initial={initialRepos} scans={scans} />
          </>
        ) : !s ? (
          !error && <div className="empty">Loading&hellip;</div>
        ) : (
          <>
            {!app ? (
              <section className="gh-sec">
                <p className="gh-lead">
                  Connect GitHub and the board moves OEMUAP items along the fix flow by itself: <b>PR review</b> while a PR is open and ready for review, <b>Developed</b> when it&rsquo;s merged into the default branch, <b>Deployed</b> when the repo&rsquo;s deploy signal says so.
                  Comments and reviews on a PR come in as notes on its items. The app is read-only: it reads metadata, pull requests, issues (GitHub sends a PR&rsquo;s comments as issue comments), checks, actions and contents (tags, commits and compare; never file contents).
                </p>
                {!s.secretKey && (
                  <p className="gh-warn">
                    <b>BOARD_SECRET_KEY isn&rsquo;t set on the server.</b> The app&rsquo;s private key and secrets are stored encrypted with it: set it (32 random bytes, base64) in App Platform and redeploy, then connect.
                  </p>
                )}
                <div className="form-grid">
                  <div>
                    <label htmlFor="gh-owner">GitHub organisation</label>
                    <input id="gh-owner" type="text" value={owner} onChange={(e) => setOwner(e.target.value)} />
                  </div>
                  <div>
                    <label htmlFor="gh-name">App name on GitHub</label>
                    <input id="gh-name" type="text" maxLength={34} value={name} onChange={(e) => setName(e.target.value)} />
                  </div>
                </div>
                <div className="row">
                  <button type="button" className="btn primary" disabled={busy || !s.secretKey || !owner.trim() || !name.trim()} onClick={connect}>
                    Connect GitHub
                  </button>
                </div>
                <p className="gh-sub">GitHub opens with the app filled in: press Create. You come back here, then install it on all repositories.</p>
              </section>
            ) : (
              <section className="gh-sec">
                {app.installed && app.missing && (app.missing.permissions.length > 0 || app.missing.events.length > 0) && (
                  <div className="gh-warn gh-grants" role="note">
                    <b>GitHub hasn&rsquo;t given the board everything it now asks for.</b> The app was connected before the board read the conversation on pull requests, so
                    comments and reviews don&rsquo;t reach it yet.
                    <ul>
                      {app.missing.permissions.length > 0 && <li>Permissions: {app.missing.permissions.join(", ")}</li>}
                      {app.missing.events.length > 0 && <li>Events: {app.missing.events.join(", ")}</li>}
                    </ul>
                    An owner of {app.owner} adds them on the app&rsquo;s{" "}
                    <a href={app.permissionsUrl} target="_blank" rel="noopener noreferrer">
                      Permissions &amp; events
                    </a>{" "}
                    page (all read-only) and saves, then accepts the request on the{" "}
                    <a href={app.acceptUrl} target="_blank" rel="noopener noreferrer">
                      installation
                    </a>
                    . This notice goes once GitHub says they&rsquo;re accepted.
                  </div>
                )}
                <dl className="gh-status">
                  <dt>Organisation</dt>
                  <dd>{app.owner}</dd>
                  <dt>App</dt>
                  <dd>
                    <a href={app.htmlUrl} target="_blank" rel="noopener noreferrer">
                      {app.name}
                    </a>{" "}
                    (id {app.appId}) · connected {fmtDate(app.connectedAt)}
                    {app.connectedBy ? ` by ${app.connectedBy}` : ""}
                  </dd>
                  <dt>Installation</dt>
                  <dd>
                    {app.installed ? (app.repoSelection === "all" ? "All repositories" : "Selected repositories (install on all for the board to see every repo)") : "Not installed yet"}{" "}
                    <a className="chip sm" href={app.installUrl} target="_blank" rel="noopener noreferrer">
                      {app.installed ? "Change on GitHub" : "Install on all repositories"} <Icon name="goto" />
                    </a>
                  </dd>
                  <dt>Last webhook</dt>
                  <dd>
                    {s.lastWebhookAt ? `${fmtDate(s.lastWebhookAt)}, ${ageShort(s.lastWebhookAt)} ago` : "None yet"}
                    {s.failures ? ` · ${s.failures} failed in the last 7 days` : ""}
                  </dd>
                  <dt>Company code</dt>
                  <dd>
                    <code>{s.code}</code>: <code>{s.code}-185</code> (the # number) in a branch name, PR title, PR description or commit message; <code>{s.code}-03-01</code> (a project number) works too. A bare #185 is GitHub&rsquo;s own.
                  </dd>
                  <dt>Stale after</dt>
                  <dd className="gh-stale">
                    <input type="number" min={1} max={30} aria-label="Days without activity before an open PR is stale" value={stale} onChange={(e) => setStale(e.target.value)} /> days without activity
                    <button type="button" className="chip sm" disabled={busy || stale === String(app.staleDays)} onClick={() => act(() => githubApi.setStaleDays(Number(stale)), "Saved.")}>
                      Save
                    </button>
                  </dd>
                  <dt>
                    <label htmlFor="gh-reviewer">Default PR reviewer</label>
                  </dt>
                  <dd className="gh-stale">
                    <select
                      id="gh-reviewer"
                      value={app.defaultReviewer?.id ?? ""}
                      disabled={busy}
                      onChange={(e) => act(() => githubApi.setDefaultReviewer(e.target.value || null), e.target.value ? "Default PR reviewer saved." : "No default PR reviewer now.")}
                    >
                      <option value="">Nobody</option>
                      {people.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                    <span className="gh-sub">who a PR in review waits for when nobody is asked to review it on GitHub</span>
                  </dd>
                </dl>
              </section>
            )}

            {/* the one place for repo facts (it loads again once the app is connected or installed) */}
            <BoardRepos key={app ? (app.installed ? "installed" : "connected") : "none"} viewer={viewer} autolink={s.autolink} toast={toast} initial={initialRepos} scans={scans} />

            <div className="fl">People and their GitHub usernames</div>
            <p className="gh-sub">A PR someone opens, merges or deploys is theirs on the board through this. Someone not here is named by their username, with a warning.</p>
            <ul className="ppl gh-people">
              {people.map((p) => (
                <Login key={p.id} person={p} onSaved={load} />
              ))}
            </ul>

            {app && (
              <>
                <div className="fl">Recent deliveries</div>
                {s.deliveries.length ? (
                  <ul className="gh-deliveries">
                    {s.deliveries.map((d) => (
                      <li key={d.id} data-failed={d.error && !d.processed ? "1" : undefined}>
                        <span className="gh-ev">
                          {d.event}
                          {d.action ? ` · ${d.action}` : ""}
                        </span>
                        <span className="gh-when">{ageShort(d.at)} ago</span>
                        <span className="gh-what">{d.error && !d.processed ? `Failed (try ${d.attempts}): ${d.error}` : (d.summary ?? (d.processed ? "done" : "waiting"))}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="gh-none">Nothing from GitHub yet.</p>
                )}
                <div className="row gh-danger">
                  <button
                    type="button"
                    className="btn"
                    disabled={busy}
                    onClick={() => {
                      if (window.confirm("Disconnect GitHub? The board stops reading it and forgets the app's secrets; PRs already linked stay on their items. Delete the app on GitHub afterwards (its settings, Advanced)."))
                        act(() => githubApi.disconnect(), "Disconnected from GitHub.");
                    }}
                  >
                    Disconnect GitHub
                  </button>
                </div>
              </>
            )}
          </>
        )}
        <div className="row">
          <button type="button" className="btn primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
}

// -- a repo's deploy signal (Settings → GitHub only) -------------------------------------------

type RuleKind = "none" | DeployRule["kind"];

/** How a repo shows a deploy (admins, Settings → GitHub): the main board and every project it's in share it. */
function DeployEditor({ repo, onSaved }: { repo: GitHubRepoView; onSaved: () => void }) {
  const [kind, setKind] = useState<RuleKind>(repo.deploy?.kind ?? "none");
  const [pattern, setPattern] = useState(repo.deploy?.kind === "tag" ? repo.deploy.pattern : "prod-*");
  const [workflow, setWorkflow] = useState(repo.deploy?.kind === "job" ? repo.deploy.workflow : "");
  const [job, setJob] = useState(repo.deploy?.kind === "job" ? repo.deploy.job : "deploy");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rule: DeployRule | null =
    kind === "tag" ? { kind, pattern: pattern.trim() } : kind === "job" ? { kind, workflow: workflow.trim(), job: job.trim() } : kind === "push" ? { kind } : null;
  const problem = rule ? deployRuleProblem(rule) : null;
  const same = JSON.stringify(rule) === JSON.stringify(repo.deploy);
  const id = `dep-${repo.id}`;
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await githubApi.setDeploy(repo.id, rule);
      onSaved();
    } catch (e) {
      setError(say(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="gh-deploy">
      <label htmlFor={id}>Deployed when</label>
      <select id={id} className="search" value={kind} onChange={(e) => setKind(e.target.value as RuleKind)}>
        <option value="none">Not set (the board doesn&rsquo;t mark its items deployed)</option>
        <option value="tag">A version tag is pushed</option>
        <option value="job">A deploy job succeeds</option>
        <option value="push">Each push to the default branch deploys</option>
      </select>
      {kind === "tag" && (
        <div className="gh-fields">
          <label>
            Tag pattern <input type="text" value={pattern} placeholder="prod-* or v*" onChange={(e) => setPattern(e.target.value)} />
          </label>
        </div>
      )}
      {kind === "job" && (
        <div className="gh-fields">
          <label>
            Workflow file <input type="text" value={workflow} placeholder="prod-release.yaml" onChange={(e) => setWorkflow(e.target.value)} />
          </label>
          <label>
            Job <input type="text" value={job} placeholder="deploy" onChange={(e) => setJob(e.target.value)} />
          </label>
        </div>
      )}
      <p className="gh-sub">
        {kind === "tag"
          ? "Items whose PRs merged into the default branch since the last matching tag are marked deployed in that tag."
          : kind === "job"
            ? "When the job succeeds, items whose PRs merged up to the run's commit are marked deployed, in the tag its build made (else the run's number)."
            : kind === "push"
              ? `For a repo whose host deploys every push to ${repo.defaultBranch}: a PR merged into it, or a commit pushed to it that names an item, marks the item developed and deployed at once.`
              : "Without one, Deployed isn't detected: its items stop at Developed. The main board and every project this repo is in share it."}
      </p>
      {problem && kind !== "none" && <span className="gh-err">{problem}</span>}
      <ErrorLine error={error} />
      <button type="button" className="chip sm" disabled={busy || same || !!problem} onClick={save}>
        Save
      </button>
    </div>
  );
}

/** One item in a scan's preview: its number, title, and what Apply would do to it. */
function ScanChange({ i }: { i: ScanItemChange }) {
  return (
    <li className="gh-change" data-changes={i.changes ? "1" : undefined}>
      <span className="gh-key">{i.label}</span>
      <span className="gh-t">{i.title}</span>
      <span className="gh-what">{i.what}</span>
    </li>
  );
}

/** What a scan of recent commits found, per commit and per open PR, and Apply. */
export function ScanPreview({ scan, busy = false, onApply, onClose }: { scan: RepoScanView; busy?: boolean; onApply: () => void; onClose: () => void }) {
  const n = scan.changes;
  const ready = n > 0 || scan.olderPending > 0;
  return (
    <section className="gh-scanv" aria-label={`Recent commits on ${scan.repo}`}>
      <p className="gh-sub">
        The last {scan.commits.length} commits on {scan.defaultBranch}, newest first. {scan.deploySaid} ({scan.reads} GitHub {scan.reads === 1 ? "read" : "reads"}.)
      </p>
      <p className="gh-sub gh-scope">
        {scan.project != null
          ? "This project's items only: items elsewhere aren't listed, and Apply doesn't touch them (scan the whole board from Settings → GitHub)."
          : "The whole board: every item the commits and PRs name, each moved only by the repos its project links (the main board's, for an item in no project)."}
      </p>
      <ol className="gh-commits">
        {scan.commits.map((c) => (
          <li key={c.sha} data-changes={c.items.some((i) => i.changes) ? "1" : undefined}>
            <span className="gh-cm">
              {c.url ? (
                <a className="gh-sha" href={c.url} target="_blank" rel="noopener noreferrer">
                  {c.short}
                </a>
              ) : (
                <code className="gh-sha">{c.short}</code>
              )}
              <span className="gh-msg">{c.message || "(no message)"}</span>
            </span>
            <span className="gh-sub">
              {c.author}
              {c.pr ? ` · PR ${c.pr.number}${c.samePr ? " (its items are listed with its newest commit)" : ""}` : ""}
            </span>
            {c.items.length > 0 ? (
              <ul className="gh-changes">
                {c.items.map((i) => (
                  <ScanChange key={i.num} i={i} />
                ))}
              </ul>
            ) : (
              !c.samePr && <span className="gh-sub gh-noitem">names no item</span>
            )}
          </li>
        ))}
      </ol>
      {scan.openPrs.length > 0 && (
        <>
          <div className="fl">Open PRs that name items</div>
          <ol className="gh-commits">
            {scan.openPrs.map((p) => (
              <li key={p.number} data-changes={p.items.some((i) => i.changes) ? "1" : undefined}>
                <span className="gh-cm">
                  <a className="gh-sha" href={p.url} target="_blank" rel="noopener noreferrer">
                    PR {p.number}
                  </a>
                  <span className="gh-msg">{p.title}</span>
                </span>
                <span className="gh-sub">
                  {p.author}
                  {p.draft ? " · a draft" : ""}
                </span>
                <ul className="gh-changes">
                  {p.items.map((i) => (
                    <ScanChange key={i.num} i={i} />
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        </>
      )}
      {scan.olderPending > 0 && (
        <p className="gh-sub">
          {scan.olderPending === 1 ? "1 PR merged before these commits isn't" : `${scan.olderPending} PRs merged before these commits aren't`} deployed yet: Apply checks {scan.olderPending === 1 ? "it" : "them"} against the deploy too.
        </p>
      )}
      <p className="gh-sub">Apply goes through the same steps as GitHub&rsquo;s webhooks, against the board as it is then: it only moves items forward, skips what a webhook already did, and each history line says it was found by a scan.</p>
      <div className="row">
        <button type="button" className="btn" onClick={onClose}>
          Close
        </button>
        <button type="button" className="btn primary" disabled={busy || !ready} onClick={onApply}>
          {n > 0 ? `Apply (${n} item${n === 1 ? "" : "s"})` : ready ? "Apply" : "Nothing to apply"}
        </button>
      </div>
    </section>
  );
}

/**
 * A repo's "Scan recent commits": the read, its preview, and Apply. For a project (Project settings,
 * leads: that project's items only), or the whole board (`projectNum` null: Settings → GitHub, admins).
 */
export function RepoScan({ projectNum, repo, toast, onApplied, initial = null }: { projectNum: number | null; repo: GitHubRepoView; toast: (m: string) => void; onApplied: () => void; initial?: RepoScanView | null }) {
  const [scan, setScan] = useState<RepoScanView | null>(initial);
  const [busy, setBusy] = useState<"scan" | "apply" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState<RepoScanApplied | null>(null);
  const run = async () => {
    setBusy("scan");
    setError(null);
    setApplied(null);
    try {
      setScan(await githubApi.scanRepo(projectNum, repo.id));
    } catch (e) {
      setError(say(e));
    } finally {
      setBusy(null);
    }
  };
  const apply = async () => {
    if (!scan) return;
    setBusy("apply");
    setError(null);
    try {
      const r = await githubApi.applyScan(projectNum, repo.id, scan.id);
      setApplied(r);
      setScan(null);
      toast(`Applied the scan of ${repoShort(repo.fullName)}: ${r.lines === 1 ? "1 history line" : `${r.lines} history lines`}.`);
      onApplied();
    } catch (e) {
      setError(say(e));
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="gh-scan">
      <div className="gh-scan-head">
        <button type="button" className="chip sm" disabled={!!busy} onClick={run}>
          {busy === "scan" ? "Scanning…" : scan ? "Scan again" : "Scan recent commits"}
        </button>
        {!scan && (
          <span className="gh-sub">
            Reads the last {SCAN_COMMITS} commits on {repo.defaultBranch} and shows what the board missed {projectNum == null ? "on the whole board" : "for this project's items"}, before anything changes.
          </span>
        )}
      </div>
      <ErrorLine error={error} />
      {applied && (
        <p className="gh-ok">
          Applied: {applied.lines === 1 ? "1 history line" : `${applied.lines} history lines`} on the items, each saying it was found by a scan.
        </p>
      )}
      {scan && <ScanPreview scan={scan} busy={busy === "apply"} onApply={apply} onClose={() => setScan(null)} />}
    </div>
  );
}

/**
 * A project's settings (leads, OEMUAP): its repos (the same links as Settings → GitHub's "Used by"),
 * how each deploys (read-only: it's set in Settings → GitHub), and a scan for this project's items.
 */
export function ProjectSettingsModal({
  project,
  viewer,
  onClose,
  toast,
  onOpenSettings,
  initial = null,
  scans = null,
}: {
  project: BoardItem;
  viewer: Viewer;
  onClose: () => void;
  toast: (m: string) => void;
  /** open Settings → GitHub, where a repo's deploy signal is changed (leads and admins) */
  onOpenSettings?: () => void;
  initial?: ProjectReposView | null;
  /** tests: a scan already shown, by repo id */
  scans?: Record<string, RepoScanView> | null;
}) {
  const [v, setV] = useState<ProjectReposView | null>(initial);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<RepoHit[] | null>(null);
  const [busy, setBusy] = useState(false);
  const leads = can(viewer, "project");
  const load = useCallback(() => githubApi.projectRepos(project.num).then(setV, (e) => setError(say(e))), [project.num]);
  useEffect(() => {
    load();
  }, [load]);
  // search the organisation's repos as they type
  useEffect(() => {
    const t = q.trim();
    if (!t || !leads) return setHits(null);
    const timer = setTimeout(() => githubApi.searchRepos(t).then(setHits, (e) => setError(say(e))), 250);
    return () => clearTimeout(timer);
  }, [q, leads]);
  const have = useMemo(() => new Set((v?.repos ?? []).map((r) => r.id)), [v]);
  const act = async (fn: () => Promise<unknown>, done?: string) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
      if (done) toast(done);
    } catch (e) {
      setError(say(e));
    } finally {
      setBusy(false);
    }
  };
  const label = project.projectNo != null ? `Project ${projectCode(project.projectNo)}` : `#${project.num}`;
  return (
    <Modal onClose={onClose} wide>
      <div className="org-dlg gh-dlg">
        <div className="dlg-eyebrow">
          <span>{label}</span>
          <span>Project settings</span>
        </div>
        <h3>{project.title}</h3>
        <div className="gh-tabs" role="tablist" aria-label="Project settings">
          <button type="button" role="tab" aria-selected="true" className="chip sm" aria-pressed="true">
            <Icon name="pr" /> Repos
          </button>
        </div>
        <p className="gh-lead">
          The board moves this project&rsquo;s items for pull requests in these repos only, even when another repo is linked to the main board. A reference from any other repo just adds a line to the item&rsquo;s history.
        </p>
        <ErrorLine error={error} />
        {!v ? (
          !error && <div className="empty">Loading&hellip;</div>
        ) : (
          <>
            {!v.connected && <p className="gh-warn">GitHub isn&rsquo;t connected yet: an admin connects it in Settings (the main board&rsquo;s cogwheel).</p>}
            {v.repos.length ? (
              <ul className="ppl gh-repos">
                {v.repos.map((r) => (
                  <li key={r.id} className="gh-repo">
                    <span className="nm">{r.fullName}</span>
                    <span className="gh-sub">
                      default branch {r.defaultBranch} · added {fmtDate(r.addedAt)}
                      {r.addedBy ? ` by ${r.addedBy}` : ""}
                    </span>
                    <span className="gh-sub gh-deployline">
                      Deploys: {deployRuleLabel(r.deploy)} ·{" "}
                      {onOpenSettings ? (
                        <button type="button" className="gh-linkish" title="How a repo deploys is set in Settings → GitHub, for the main board and every project it's in" onClick={onOpenSettings}>
                          change in Settings
                        </button>
                      ) : (
                        "change in Settings → GitHub"
                      )}
                      {r.mainBoard ? " · also on the main board" : ""}
                    </span>
                    {r.noDeploy && <NoDeployFlag />}
                    {leads && (
                      <button type="button" className="chip sm rm" disabled={busy} onClick={() => act(() => githubApi.removeRepo(project.num, r.id), `Took ${repoShort(r.fullName)} off the project.`)}>
                        Remove
                      </button>
                    )}
                    {leads && v.connected && <RepoScan projectNum={project.num} repo={r} toast={toast} onApplied={load} initial={scans?.[r.id] ?? null} />}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="gh-none">No repos yet.</p>
            )}
            {leads && v.connected && (
              <>
                <div className="fl">Add a repo</div>
                <input className="search" type="search" placeholder="Search the organisation's repos…" aria-label="Search the organisation's repos" value={q} onChange={(e) => setQ(e.target.value)} />
                {hits && (
                  <ul className="gh-hits">
                    {hits.map((h) => (
                      <li key={h.id}>
                        <span className="nm">{h.fullName}</span>
                        <span className="gh-sub">
                          {h.private ? "private" : "public"}
                          {h.archived ? " · archived" : ""}
                        </span>
                        <button type="button" className="chip sm" disabled={busy || have.has(h.id)} onClick={() => act(() => githubApi.addRepo(project.num, h.id), `Linked ${repoShort(h.fullName)} to the project.`)}>
                          {have.has(h.id) ? "Linked" : "Add"}
                        </button>
                      </li>
                    ))}
                    {!hits.length && <li className="gh-none">No repo matches.</li>}
                  </ul>
                )}
              </>
            )}
          </>
        )}
        <div className="row">
          <button type="button" className="btn primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
}

// -- the project's board: recent PRs nobody has ------------------------------------------------

/** Recent PRs in the project's repos that no item has, each attachable in one click. Nothing when there are none. */
export function UnlinkedPrs({ project, items, onAttached, toast, initial = null }: { project: BoardItem; items: BoardItem[]; onAttached: () => void; toast: (m: string) => void; initial?: UnlinkedPr[] | null }) {
  const [list, setList] = useState<UnlinkedPr[] | null>(initial);
  const [pick, setPick] = useState<Record<string, number>>({});
  const load = useCallback(() => githubApi.unlinked(project.num).then(setList, () => setList([])), [project.num]);
  useEffect(() => {
    load();
  }, [load, items]);
  const open = items.filter((i) => i.kind !== "project" && i.status === "open" && !i.archived);
  if (!list?.length || !open.length) return null;
  const attach = async (p: UnlinkedPr) => {
    const num = pick[p.id] ?? open[0]!.num;
    try {
      await githubApi.linkPr(num, p.url);
      toast(`Attached PR ${p.number} to ${githubRef(num)}.`);
      onAttached();
      load();
    } catch (e) {
      toast(say(e));
    }
  };
  return (
    <section className="gh-unlinked" aria-label="Recent pull requests no item has">
      <div className="section-head">
        <Icon name="pr" /> Recent PRs in this project&rsquo;s repos that no item has &middot; {list.length}
      </div>
      <ul>
        {list.slice(0, 8).map((p) => (
          <li key={p.id}>
            <a className="prchip" data-tone={p.state === "merged" ? "merged" : p.state === "closed" ? "closed" : p.draft ? "draft" : "open"} href={p.url} target="_blank" rel="noopener noreferrer">
              <Icon name="pr" />
              <span>
                PR {p.number} · {p.state}
              </span>
            </a>
            <span className="gh-t">{p.title}</span>
            <span className="gh-sub">
              {repoShort(p.repo)} · {p.author} · {fmtDate(p.openedAt)}
            </span>
            <span className="gh-attach">
              <select className="search" aria-label={`Item to attach PR ${p.number} to`} value={pick[p.id] ?? open[0]!.num} onChange={(e) => setPick({ ...pick, [p.id]: Number(e.target.value) })}>
                {open.map((i) => (
                  <option key={i.num} value={i.num}>
                    {i.key?.label ?? `#${i.num}`} {i.title.slice(0, 60)}
                  </option>
                ))}
              </select>
              <button type="button" className="chip sm" onClick={() => attach(p)}>
                Attach
              </button>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
